import { FunctionComponent, useState } from 'react'
import styles from './addGroupDialog.module.scss'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import { useMutation } from '@apollo/client'
import { CREATE_DAYCARE_GROUP } from '@/modules/API/daycareGroup/daycareGroupHandler'
import { DaycareGroupType } from '@/types/DaycareGroup'
import { SelectOptionsDeprecated } from '@/types/Select'
import { useTranslation } from 'react-i18next'
import UserInput from '@/components/_custom/UserInput/userInput'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import SelectUnique from '@/components/_custom/Selector/selectUnique/selectUnique'
import Title from '@/components/_custom/Title/title'
import CountrySelect from '@/components/_custom/CountrySelect/countrySelect'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface AddGroupDialogProps {
    open: boolean
    handleClose: () => void
    refetchGroups: () => void
}

const AddGroupDialog: FunctionComponent<AddGroupDialogProps> = ({
    open,
    handleClose,
    refetchGroups
}: AddGroupDialogProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [groupName, setGroupName] = useState<string>('')
    const [groupLocation, setGroupLocation] = useState<string>('')
    const [groupCountry, setGroupCountry] = useState<string>('')
    const [groupType, setGroupType] = useState<DaycareGroupType>(DaycareGroupType.city)

    const [adminEmail, setAdminEmail] = useState<string>('')
    const [validEmail, setValidEmail] = useState<boolean>(false)
    const [adminTel, setAdminTel] = useState<string>('')
    const [adminLastName, setAdminLastName] = useState<string>('')
    const [adminFirstName, setAdminFirstName] = useState<string>('')

    const [createDaycareGroup] = useMutation(CREATE_DAYCARE_GROUP, {
        variables: {
            group: {
                name: groupName,
                location: groupLocation,
                country: groupCountry,
                type: groupType
            },
            trustee: {
                email: adminEmail,
                phone: adminTel,
                lastname: adminLastName,
                firstname: adminFirstName
            }
        },
        onCompleted: () => {
            showNotificationSuccess(t('groupCreated'))

            refetchGroups()
            handleClose()
        },
        onError: () => {
            showNotificationError(t('errorCreatingGroup'))
        }
    })

    function setGroupTypeConverter(value: string): void {
        switch (value) {
            case DaycareGroupType.agglomeration:
                setGroupType(DaycareGroupType.agglomeration)
                break
            case DaycareGroupType.association:
                setGroupType(DaycareGroupType.association)
                break
            case DaycareGroupType.city:
                setGroupType(DaycareGroupType.city)
                break
            case DaycareGroupType.private:
                setGroupType(DaycareGroupType.private)
                break
            case DaycareGroupType.community:
                setGroupType(DaycareGroupType.community)
                break
            case DaycareGroupType.county:
                setGroupType(DaycareGroupType.county)
                break
        }
    }

    const selectDaycareType: SelectOptionsDeprecated = [
        {
            options: [
                { value: DaycareGroupType.agglomeration, name: t('agglomeration') },
                { value: DaycareGroupType.association, name: t('association') },
                { value: DaycareGroupType.community, name: t('community') },
                { value: DaycareGroupType.private, name: t('private') },
                { value: DaycareGroupType.county, name: t('county') },
                { value: DaycareGroupType.city, name: t('city') }
            ]
        }
    ]

    const disabled: boolean =
        !groupName ||
        !groupCountry ||
        !groupLocation ||
        !adminEmail ||
        !adminTel ||
        !adminLastName ||
        !adminFirstName ||
        !validEmail

    return (
        <Dialog open={open} handleClose={handleClose}>
            <DialogHeader title={t('addGroup')} />
            <DialogBody className={styles.DialogBody}>
                <div className={styles.InputContainer}>
                    <Title text={t('groupInformation')} className={styles.Title} />
                    <UserInput
                        label={t('name')}
                        className={styles.UserInput}
                        value={groupName}
                        setValue={setGroupName}
                        format={'pascal'}
                        required
                    />
                    <div className={styles.UserInput}>
                        <CountrySelect country={groupCountry} setCountry={setGroupCountry} required />
                    </div>
                    <UserInput
                        label={t('location')}
                        className={styles.UserInput}
                        value={groupLocation}
                        setValue={setGroupLocation}
                        format={'upper'}
                        unallowedCharacters={'num'}
                        required
                    />
                    <SelectUnique
                        label={t('type')}
                        setSelectValue={setGroupTypeConverter}
                        selectValue={groupType}
                        selectOptions={selectDaycareType}
                        formClassName={styles.SelectInput}
                    />
                </div>
                <div className={styles.InputContainer}>
                    <Title text={t('adminInformation')} className={styles.Title} />
                    <UserInput
                        label={t('lastname')}
                        className={styles.UserInput}
                        value={adminLastName}
                        setValue={setAdminLastName}
                        format={'upper'}
                        required
                    />
                    <UserInput
                        label={t('firstname')}
                        className={styles.UserInput}
                        value={adminFirstName}
                        setValue={setAdminFirstName}
                        format={'pascal'}
                        required
                    />
                    <UserInput
                        label={t('email')}
                        className={styles.UserInput}
                        value={adminEmail}
                        setValue={setAdminEmail}
                        verify={'email'}
                        type='text'
                        unallowedCharacters={'email'}
                        setValid={setValidEmail}
                        required
                    />
                    <UserInput
                        label={t('phone')}
                        className={styles.UserInput}
                        value={adminTel}
                        setValue={setAdminTel}
                        format={'phone'}
                        required
                    />
                </div>
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon type={'close'} onClick={handleClose} className={styles.Button} />
                    <ButtonTextIcon
                        type={'add'}
                        onClick={createDaycareGroup}
                        disabled={disabled}
                        className={styles.Button}
                    />
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default AddGroupDialog
